// Reusable renderer for the Leave / Expense reports. Keeps styling and
// filters identical across both so HR has one mental model for every report.
import React, { useEffect, useMemo, useState } from 'react';
import api, { companiesAPI, employeesAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  CalendarDays, Download, Filter, Loader2, RefreshCcw,
  FileSpreadsheet, FileText, FileType2, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const PRESETS = [
  { v: 'today', l: 'Today' },
  { v: 'yesterday', l: 'Yesterday' },
  { v: 'this_week', l: 'This Week' },
  { v: 'last_week', l: 'Last Week' },
  { v: 'this_month', l: 'This Month' },
  { v: 'last_month', l: 'Last Month' },
  { v: 'custom', l: 'Custom Range' },
];

const statusTint = (status, isExpense) => {
  const s = (status || '').toLowerCase();
  if (s === 'approved' || s === 'paid' || s === 'finance approved')
    return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (s === 'manager approved' || s === 'hr approved')
    return 'bg-sky-100 text-sky-700 border-sky-200';
  if (s === 'pending')
    return 'bg-amber-100 text-amber-700 border-amber-200';
  if (s === 'rejected' || s === 'cancelled')
    return 'bg-red-100 text-red-700 border-red-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

export default function HrReportPage({ type }) {
  const { user } = useAuth() || {};
  const isManager = user?.role === 'manager';
  const isExpense = type === 'expense';
  const title = isExpense ? 'Expense Report' : 'Leave Report';
  const endpoint = isExpense ? '/reports/expense-report' : '/reports/leave-report';
  const statusOptions = isExpense
    ? ['pending', 'manager_approved', 'hr_approved', 'approved', 'finance_approved', 'paid', 'rejected']
    : ['pending', 'manager_approved', 'approved', 'rejected', 'cancelled'];

  const [preset, setPreset] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [companyId, setCompanyId] = useState('all');
  const [department, setDepartment] = useState('all');
  const [employeeId, setEmployeeId] = useState('all');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('');

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [cRes, eRes] = await Promise.all([
          companiesAPI.getAll().catch(() => ({ data: [] })),
          employeesAPI.getAll({ limit: 2000 }).catch(() => ({ data: [] })),
        ]);
        setCompanies(cRes.data || []);
        setEmployees(eRes.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const departments = useMemo(() => {
    const s = new Set();
    (employees || []).forEach((e) => e.department && s.add(e.department));
    return Array.from(s).sort();
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    let list = employees;
    if (companyId !== 'all') list = list.filter((e) => e.company_id === companyId);
    if (department !== 'all') list = list.filter((e) => e.department === department);
    return list;
  }, [employees, companyId, department]);

  const buildParams = (fmt) => {
    const p = { preset };
    if (preset === 'custom') {
      if (!startDate || !endDate) { toast.error('Pick a start and end date'); return null; }
      p.start_date = startDate; p.end_date = endDate;
    }
    if (companyId !== 'all') p.company_id = companyId;
    if (department !== 'all') p.department = department;
    if (employeeId !== 'all') p.employee_id = employeeId;
    if (status !== 'all') p.status = status;
    if (isExpense && category) p.category = category;
    if (fmt) p.format = fmt;
    return p;
  };

  const runReport = async () => {
    const params = buildParams();
    if (!params) return;
    setLoading(true);
    try {
      const res = await api.get(endpoint, { params: { ...params, limit: 300 } });
      setRows(res.data?.rows || []);
      setSummary(res.data?.summary || null);
      setRange(res.data?.range || null);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      setRows([]); setSummary(null); setRange(null); setTotal(0);
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runReport(); }, [type]);

  const doExport = async (fmt) => {
    const params = buildParams(fmt);
    if (!params) return;
    setDownloading(fmt);
    try {
      const res = await api.get(endpoint + '/export',
        { params, responseType: 'blob' });
      const stamp = `${params.start_date || range?.start || 'range'}_${params.end_date || range?.end || ''}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type}_report_${stamp}.${fmt}`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setDownloading(''); }
  };

  const summaryTiles = isExpense ? [
    { k: 'total',             l: 'Rows',             c: 'text-slate-900' },
    { k: 'approved',          l: 'Approved',         c: 'text-emerald-600' },
    { k: 'finance_approved',  l: 'Finance OK',       c: 'text-emerald-700' },
    { k: 'paid',              l: 'Paid',             c: 'text-sky-700' },
    { k: 'manager_approved',  l: 'Mgr Approved',     c: 'text-sky-600' },
    { k: 'pending',           l: 'Pending',          c: 'text-amber-600' },
    { k: 'rejected',          l: 'Rejected',         c: 'text-red-600' },
  ] : [
    { k: 'total_requests',    l: 'Requests',         c: 'text-slate-900' },
    { k: 'approved',          l: 'Approved',         c: 'text-emerald-600' },
    { k: 'manager_approved',  l: 'Mgr Approved',     c: 'text-sky-600' },
    { k: 'pending',           l: 'Pending',          c: 'text-amber-600' },
    { k: 'rejected',          l: 'Rejected',         c: 'text-red-600' },
    { k: 'cancelled',         l: 'Cancelled',        c: 'text-zinc-600' },
    { k: 'total_days_approved', l: 'Approved Days',  c: 'text-slate-900' },
  ];

  const approvedTotalsLine = isExpense && summary?.approved_totals_by_currency
    ? Object.entries(summary.approved_totals_by_currency)
        .map(([c, a]) => `${c} ${a}`).join(' | ')
    : null;

  return (
    <div className="space-y-6" data-testid={`${type}-report-page`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            {title}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {isManager ? "Your team's " : 'Enterprise '}
            {isExpense ? 'expense claims' : 'leave requests'} with Excel, CSV and PDF export.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runReport} disabled={loading}
                  data-testid={`${type}-report-refresh`}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                     : <RefreshCcw className="w-4 h-4 mr-2" />}
            Run Report
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={!!downloading || loading}
                      data-testid={`${type}-report-export-btn`}>
                {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                             : <Download className="w-4 h-4 mr-2" />}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-50">
              <DropdownMenuItem onClick={() => doExport('xlsx')}
                data-testid={`${type}-report-export-xlsx`}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport('csv')}
                data-testid={`${type}-report-export-csv`}>
                <FileText className="w-4 h-4 mr-2" /> CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => doExport('pdf')}
                data-testid={`${type}-report-export-pdf`}>
                <FileType2 className="w-4 h-4 mr-2" /> PDF (.pdf)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <Card data-testid={`${type}-report-filters`}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Range</Label>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger data-testid={`${type}-report-preset`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRESETS.map((p) => (<SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {preset === 'custom' && (
              <>
                <div>
                  <Label className="text-xs text-slate-600">Start Date</Label>
                  <Input type="date" value={startDate}
                         onChange={(e) => setStartDate(e.target.value)}
                         data-testid={`${type}-report-start-date`} />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">End Date</Label>
                  <Input type="date" value={endDate}
                         onChange={(e) => setEndDate(e.target.value)}
                         data-testid={`${type}-report-end-date`} />
                </div>
              </>
            )}
            {!isManager && (
              <div>
                <Label className="text-xs text-slate-600">Company</Label>
                <Select value={companyId} onValueChange={(v) => { setCompanyId(v); setEmployeeId('all'); }}>
                  <SelectTrigger data-testid={`${type}-report-company`}><SelectValue placeholder="All Companies" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-600">Department</Label>
              <Select value={department} onValueChange={(v) => { setDepartment(v); setEmployeeId('all'); }}>
                <SelectTrigger data-testid={`${type}-report-department`}><SelectValue placeholder="All Departments" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Employee</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger data-testid={`${type}-report-employee`}><SelectValue placeholder="All Employees" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Employees</SelectItem>
                  {filteredEmployees.slice(0, 500).map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.employee_code ? `${e.employee_code} — ` : ''}{e.first_name} {e.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid={`${type}-report-status`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statusOptions.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isExpense && (
              <div>
                <Label className="text-xs text-slate-600">Category</Label>
                <Input placeholder="e.g. Travel, Food"
                       value={category} onChange={(e) => setCategory(e.target.value)}
                       data-testid={`${type}-report-category`} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3"
             data-testid={`${type}-report-summary`}>
          {summaryTiles.map((t) => (
            <Card key={t.k}>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500">{t.l}</div>
                <div className={`text-2xl font-semibold ${t.c}`}>
                  {summary[t.k] ?? 0}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {approvedTotalsLine && (
        <div className="text-xs text-slate-500" data-testid="expense-approved-totals">
          Approved totals (by currency): <b className="text-slate-700">{approvedTotalsLine}</b>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="w-4 h-4" />
            {range ? `Preview ${range.start} → ${range.end}` : 'Preview'}
          </CardTitle>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Users className="w-3.5 h-3.5" />
            Showing <b>{rows.length}</b> of <b>{total}</b> rows
            {total > rows.length && ' — export for the full dataset'}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="py-10 text-center text-slate-400">
              <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-400"
                 data-testid={`${type}-report-empty`}>
              No data for the selected filters.
            </div>
          ) : (
            <Table data-testid={`${type}-report-table`}>
              <TableHeader>
                <TableRow>
                  {isExpense && <TableHead>Date</TableHead>}
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Dept</TableHead>
                  {isExpense ? <TableHead>Category</TableHead>
                             : <TableHead>Leave Type</TableHead>}
                  {!isExpense && <TableHead>Start</TableHead>}
                  {!isExpense && <TableHead>End</TableHead>}
                  {!isExpense && <TableHead>Days</TableHead>}
                  {isExpense && <TableHead className="text-right">Amount</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell">
                    {isExpense ? 'Description' : 'Reason'}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    {isExpense && <TableCell>{r.Date}</TableCell>}
                    <TableCell>{r['Employee Code']}</TableCell>
                    <TableCell className="whitespace-nowrap">{r['Employee Name']}</TableCell>
                    <TableCell>{r.Department}</TableCell>
                    {isExpense
                      ? <TableCell>{r.Category}</TableCell>
                      : <TableCell>{r['Leave Type']}</TableCell>}
                    {!isExpense && <TableCell>{r['Start Date']}</TableCell>}
                    {!isExpense && <TableCell>{r['End Date']}</TableCell>}
                    {!isExpense && <TableCell>{r.Days}</TableCell>}
                    {isExpense && (
                      <TableCell className="text-right font-mono">
                        {r.Amount} <span className="text-slate-400">{r.Currency}</span>
                      </TableCell>
                    )}
                    <TableCell>
                      <Badge variant="outline" className={statusTint(r.Status, isExpense)}>
                        {r.Status}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-slate-500 max-w-xs truncate">
                      {isExpense ? r.Description : r.Reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
